import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const FAQScroll = () => {
    const sectionRef = useRef(null);
    const faqItemsRef = useRef([]);
    const titleRef = useRef(null);
    const [openIndex, setOpenIndex] = useState(null);

    const faqs = [
        {
            question: "How long does the visa processing take?",
            answer: "Visa processing typically takes 4-8 weeks depending on the country and time of year. We provide regular updates throughout the process."
        },
        {
            question: "What is your visa success rate?",
            answer: "We have a 95%+ visa success rate, backed by our expert guidance and thorough documentation process."
        },
        {
            question: "What are the costs involved for your services?",
            answer: "Our consultation fees are competitive and transparent. We offer flexible payment plans and free initial consultation for serious students."
        },
        {
            question: "What documents are required for visa application?",
            answer: "Required documents include academic transcripts, passport, financial documents, English proficiency test scores (IELTS/PTE), and admission letters from universities."
        },
        {
            question: "Do you provide scholarship assistance?",
            answer: "Yes! We help students find and apply for various scholarships, grants, and financial aid options available for international students."
        },
        {
            question: "Can I work part-time while studying?",
            answer: "Most student visas allow part-time work (20 hours/week). We guide you on work regulations specific to your destination country."
        },
        {
            question: "What is post-study work visa options?",
            answer: "Countries like UK, Canada, Australia, and New Zealand offer post-study work visas ranging from 2-4 years, allowing you to gain international work experience."
        },
        {
            question: "Do you help with accommodation?",
            answer: "Yes, we provide pre-departure orientation including accommodation options, airport pickup coordination, and local support in your destination city."
        }
    ];

    const toggleFAQ = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    useEffect(() => {
        const ctx = gsap.context(() => {
            // Title animation with scale effect
            gsap.fromTo(
                titleRef.current,
                {
                    opacity: 0,
                    y: 50,
                    scale: 0.9,
                },
                {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    duration: 1,
                    ease: 'power3.out',
                    scrollTrigger: {
                        trigger: sectionRef.current,
                        start: 'top 80%',
                        end: 'top 50%',
                        scrub: 1,
                    },
                }
            );

            // FAQ items staggered animation with slide effect
            faqItemsRef.current.forEach((item, index) => {
                gsap.fromTo(
                    item,
                    {
                        opacity: 0,
                        x: index % 2 === 0 ? -50 : 50,
                        scale: 0.95,
                    },
                    {
                        opacity: 1,
                        x: 0,
                        scale: 1,
                        duration: 0.8,
                        ease: 'power3.out',
                        scrollTrigger: {
                            trigger: item,
                            start: 'top 90%',
                            end: 'top 60%',
                            scrub: 1,
                        },
                    }
                );
            });
        }, sectionRef);

        return () => ctx.revert();
    }, []);

    return (
        <section
            ref={sectionRef}
            id="faq"
            className="py-20 bg-lightGray relative overflow-hidden"
        >
            {/* Animated background pattern */}
            <div className="absolute inset-0 opacity-5">
                <div className="absolute inset-0" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23E63946' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                }}></div>
            </div>

            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {/* Section Header with kinetic typography */}
                <div ref={titleRef} className="text-center mb-16">
                    <div className="flex items-center justify-center gap-3 mb-4">
                        <i className="fas fa-question-circle text-primary-red text-2xl"></i>
                        <h2 className="section-title">Frequently Asked Questions</h2>
                    </div>
                    <p className="section-subtitle">Get answers to common questions about studying abroad</p>
                </div>

                {/* FAQ Items with staggered animations */}
                <div className="space-y-4 mt-12">
                    {faqs.map((faq, index) => (
                        <div
                            key={index}
                            ref={(el) => (faqItemsRef.current[index] = el)}
                            className="faq-item bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow duration-300"
                        >
                            <button
                                className="w-full px-6 py-4 text-left flex items-center justify-between focus:outline-none"
                                onClick={() => toggleFAQ(index)}
                                aria-expanded={openIndex === index}
                            >
                                <span className="font-semibold text-black">{faq.question}</span>
                                <i className={`fas ${openIndex === index ? 'fa-chevron-up' : 'fa-chevron-down'} text-primary-red transition-transform duration-300`}></i>
                            </button>
                            {openIndex === index && (
                                <div className="px-6 pb-4 animate-fade-in">
                                    <p className="text-gray">{faq.answer}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default FAQScroll;
